/**
 * <compare-popup> — comparison table modal.
 * Product handles are stored in localStorage by compare-add-button.js;
 * on open, each product is fetched from its public /products/{handle}.js
 * endpoint and rows are built with DOM APIs (textContent — no innerHTML
 * with remote data). Add-to-cart uses the theme's global CartAPI.
 */
if (!customElements.get('compare-popup')) {
    class ComparePopup extends HTMLElement {
        connectedCallback() {
            this.body = this.querySelector('[data-compare-body]');
            this.closeBtn = this.querySelector('.modal__toggle-close');
            this.overlay = this.querySelector('.modal__overlay');
            this.onKeydown = (e) => {
                if (e.key === 'Escape') this.close();
            };

            this.closeBtn?.addEventListener('click', () => this.close());
            this.overlay?.addEventListener('click', () => this.close());

            this.body?.addEventListener('click', (e) => this.onBodyClick(e));
        }

        open() {
            this.previouslyFocused = document.activeElement;
            this.setAttribute('aria-hidden', 'false');
            document.body.setAttribute('data-compare-opened', 'true');
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', this.onKeydown);
            this.renderRows();
            if (window.Utils && typeof window.Utils.trapFocus === 'function') {
                window.Utils.trapFocus(this, this.closeBtn || this);
            }
        }

        close() {
            this.setAttribute('aria-hidden', 'true');
            document.body.removeAttribute('data-compare-opened');
            document.body.style.overflow = '';
            document.removeEventListener('keydown', this.onKeydown);
            if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
                this.previouslyFocused.focus();
                this.previouslyFocused = null;
            }
        }

        getHandles() {
            try {
                return JSON.parse(CompareUtils.getCompareItems() || '[]');
            } catch (e) {
                return [];
            }
        }

        showWarning(count) {
            const container = this.querySelector('.modal__popup-warnings');
            if (!container) return;
            const span = container.querySelector('.warning');

            if (count >= 2) {
                container.setAttribute('hidden', '');
                return;
            }
            container.removeAttribute('hidden');
            if (span) {
                span.textContent = count === 0 ? span.dataset.emptyList : span.dataset.lackList;
            }
        }

        async renderRows() {
            if (!this.body) return;
            const handles = this.getHandles();
            this.showWarning(handles.length);
            this.classList.add('loading');

            const products = (
                await Promise.all(
                    handles.map((handle) =>
                        fetch(`${window.Shopify.routes.root}products/${encodeURIComponent(handle)}.js`)
                            .then((r) => (r.ok ? r.json() : null))
                            .catch(() => null)
                    )
                )
            ).filter(Boolean);

            this.classList.remove('loading');
            this.body.replaceChildren();

            if (products.length === 0) {
                const row = this.body.insertRow();
                row.className = 'compare-table__empty-state';
                const cell = row.insertCell();
                cell.colSpan = 6;
                cell.className = 'text-center';
                cell.textContent = this.body.dataset.textEmpty;
                return;
            }

            products.forEach((product) => this.body.appendChild(this.buildRow(product)));
        }

        buildRow(product) {
            const d = this.body.dataset;
            const row = document.createElement('tr');
            row.className = 'compare-table__row';

            // Product cell: image + linked title
            const productCell = row.insertCell();
            productCell.className = 'compare-table__product';
            const link = document.createElement('a');
            link.href = product.url;
            link.className = 'compare-table__product-link';
            if (product.featured_image) {
                const img = document.createElement('img');
                img.src = product.featured_image + '&width=120';
                img.width = 60;
                img.height = 60;
                img.loading = 'lazy';
                img.alt = '';
                link.appendChild(img);
            }
            const title = document.createElement('span');
            title.textContent = product.title;
            link.appendChild(title);
            productCell.appendChild(link);

            // Price
            const priceCell = row.insertCell();
            priceCell.className = 'compare-table__price';
            priceCell.textContent = this.formatMoney(product.price);

            if (d.showVendor === 'true') {
                const vendorCell = row.insertCell();
                vendorCell.textContent = product.vendor || '—';
            }

            if (d.showDescription === 'true') {
                const descCell = row.insertCell();
                descCell.className = 'compare-table__description';
                const tmp = document.createElement('div');
                tmp.innerHTML = product.description || '';
                const text = tmp.textContent.trim();
                descCell.textContent = text.length > 140 ? text.slice(0, 140) + '…' : text;
            }

            if (d.showAddToCart === 'true') {
                const actionCell = row.insertCell();
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'btn btn--primary compare-table__add';
                const variant = product.variants.find((v) => v.available) || product.variants[0];
                if (variant && variant.available) {
                    btn.textContent = d.textAdd;
                    btn.dataset.variantId = variant.id;
                } else {
                    btn.textContent = d.textSoldOut;
                    btn.disabled = true;
                }
                actionCell.appendChild(btn);
            }

            // Remove
            const removeCell = row.insertCell();
            const removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.className = 'compare-table__remove';
            removeBtn.dataset.removeHandle = product.handle;
            removeBtn.setAttribute('aria-label', `${d.textRemove} ${product.title}`);
            removeBtn.textContent = '×';
            removeCell.appendChild(removeBtn);

            return row;
        }

        async onBodyClick(e) {
            const removeBtn = e.target.closest('[data-remove-handle]');
            if (removeBtn) {
                const handles = this.getHandles().filter((h) => h !== removeBtn.dataset.removeHandle);
                CompareUtils.setCompareItems(handles);
                document.dispatchEvent(new CustomEvent('compare:updated'));
                this.renderRows();
                return;
            }

            const addBtn = e.target.closest('[data-variant-id]');
            if (addBtn && window.cart) {
                addBtn.disabled = true;
                try {
                    await window.cart.addItem(addBtn.dataset.variantId, 1);
                    this.close();
                    if (window.PubSub) window.PubSub.publish('cart:open-drawer');
                } finally {
                    addBtn.disabled = false;
                }
            }
        }

        formatMoney(cents) {
            const format = window.themeSettings?.moneyFormat || '${{amount}}';
            const amount = (cents / 100).toFixed(2);
            return format.replace(/\{\{\s*amount[^}]*\}\}/, amount).replace(/<[^>]+>/g, '');
        }
    }

    customElements.define('compare-popup', ComparePopup);

    // Header compare icon (and any [data-compare-open] trigger) opens the popup.
    document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-compare-open]');
        if (trigger) {
            document.querySelector('compare-popup')?.open();
        }
    });
}
